import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Alert, Card, Form, Spinner } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import BannerCard from "../components/BannerCard";
import BannerModal from "../components/BannerModal";
import {
  whoami,
  getAllBannerContent,
  createBannerContent,
  updateBannerContent,
  deleteBannerContent,
  getCountries,
  getBannerContentByType,
} from "../services/APIClient";
import "../components/SchoolAdmin.css";
import "../components/BannerContentAdmin.css";
import { FaPlus, FaSearch } from "react-icons/fa";

function BannerContentAdmin() {
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("Admin User");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  const [banners, setBanners] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedSchoolName, setSelectedSchoolName] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load user info on mount
  useEffect(() => {
    whoami().then((response) => {
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
    });
  }, []);

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      setCountriesLoading(true);
      try {
        const response = await getCountries();
        const countryList = response.data || response;
        setCountries(Array.isArray(countryList) ? countryList : []);
      } catch (err) {
        console.error("Error loading countries:", err);
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  // Load all banners on mount
  useEffect(() => {
    loadBanners();
  }, []);

  // Filter banners based on search and selected filters
  useEffect(() => {
    let filtered = banners;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((banner) =>
        banner.banner_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        banner.header.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by country
    if (selectedCountry) {
      filtered = filtered.filter((banner) => banner.locale?.startsWith(selectedCountry));
    }

    // Filter by school
    if (selectedSchool) {
      filtered = filtered.filter((banner) => banner.schoolid === selectedSchool);
    }

    setFilteredBanners(filtered);
  }, [searchTerm, banners, selectedCountry, selectedSchool]);

  const loadBanners = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllBannerContent();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load banners. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setEditingBanner(null);
    setShowModal(true);
  };

  const handleEditClick = (banner) => {
    setEditingBanner(banner);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingBanner(null);
  };

  const handleSaveBanner = async (bannerData) => {
    setIsSaving(true);
    try {
      if (editingBanner && editingBanner.id) {
        // For updates, only send updateable fields and filter out null/undefined
        const updateData = {
          header: bannerData.header,
          subheader: bannerData.subheader || undefined,
          content: bannerData.content || undefined,
          link_url: bannerData.link_url || undefined,
          button_accept_label: bannerData.button_accept_label || undefined,
          button_cancel_label: bannerData.button_cancel_label || undefined,
          locale: bannerData.locale || 'en-IE',
          is_active: bannerData.is_active !== undefined ? bannerData.is_active : true,
          updated_at: new Date(),
          updated_by: teacherid,
        };
        
        // Remove undefined keys so they aren't sent to API
        const cleanUpdateData = Object.fromEntries(
          Object.entries(updateData).filter(([, value]) => value !== undefined)
        );
        
        console.log("🧹 Cleaned update data:", cleanUpdateData);
        await updateBannerContent(editingBanner.id, cleanUpdateData);
      } else {
        // For creates, include all fields but filter out undefined
        const createData = {
          banner_type: bannerData.banner_type,
          countryid: bannerData.countryid || undefined,
          header: bannerData.header,
          subheader: bannerData.subheader || undefined,
          content: bannerData.content || undefined,
          link_url: bannerData.link_url || undefined,
          button_accept_label: bannerData.button_accept_label || undefined,
          button_cancel_label: bannerData.button_cancel_label || undefined,
          locale: bannerData.locale || 'en-IE',
          is_active: bannerData.is_active !== undefined ? bannerData.is_active : true,
          created_at: new Date(),
          created_by: teacherid,
          updated_at: new Date(),
          updated_by: teacherid,
        };
        
        // Remove undefined keys so they aren't sent to API
        const cleanCreateData = Object.fromEntries(
          Object.entries(createData).filter(([, value]) => value !== undefined)
        );
        
        console.log("🧹 Cleaned create data:", cleanCreateData);
        await createBannerContent(cleanCreateData);
      }
      await loadBanners();
      handleModalClose();
    } catch (err) {
      setError("Failed to save banner. Please try again.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) {
      return;
    }
    try {
      await deleteBannerContent(id);
      await loadBanners();
    } catch (err) {
      setError("Failed to delete banner. Please try again.");
      console.error(err);
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await updateBannerContent(banner.id, {
        ...banner,
        is_active: !banner.is_active,
      });
      await loadBanners();
    } catch (err) {
      setError("Failed to update banner status. Please try again.");
      console.error(err);
    }
  };

  const handleSchoolRetrieved = (schoolId) => {
    setSelectedSchool(schoolId);
  };

  const handleSchoolNameRetrieved = (schoolName) => {
    setSelectedSchoolName(schoolName);
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner
          name={name}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">
            {/* Page Header */}
            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Banner Content Management</h2>
              <p className="class-admin-page-subtitle">
                Create and manage banner messaging across the platform
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" className="mb-4">
                <strong>Error:</strong> {error}
                <Button
                  variant="link"
                  className="ms-2"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </Alert>
            )}

            {/* Filters Section */}
            <Card className="mb-4">
              <Card.Body>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Filter by Country</Form.Label>
                      <Form.Select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        disabled={countriesLoading}
                      >
                        <option value="">All Countries</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name} ({country.id})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Filter by School</Form.Label>
                      <SchoolRollNumberInput
                        onSchoolIdRetrieved={handleSchoolRetrieved}
                        schoolNameRetrieved={handleSchoolNameRetrieved}
                        label="Search School"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Search Banners</Form.Label>
                      <div className="banner-search-container">
                        <FaSearch className="banner-search-icon" />
                        <Form.Control
                          type="text"
                          placeholder="Search by type or header..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="banner-search-input"
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                {(selectedCountry || selectedSchool) && (
                  <Row className="mt-3">
                    <Col>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSelectedCountry("");
                          setSelectedSchool("");
                          setSelectedSchoolName("");
                        }}
                      >
                        Clear Filters
                      </Button>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Create Button */}
            <Row className="mb-4">
              <Col className="text-end">
                <Button
                  variant="primary"
                  onClick={handleCreateClick}
                  className="banner-create-btn"
                >
                  <FaPlus className="me-2" /> Create Banner
                </Button>
              </Col>
            </Row>

            {/* Loading State */}
            {loading ? (
              <Row className="banner-loading">
                <Col className="text-center">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Loading banners...</p>
                </Col>
              </Row>
            ) : filteredBanners.length === 0 ? (
              <Row>
                <Col className="text-center banner-empty">
                  <p className="text-muted">
                    {searchTerm || selectedCountry || selectedSchool
                      ? "No banners found matching your filters."
                      : "No banners yet. Create one to get started."}
                  </p>
                </Col>
              </Row>
            ) : (
              <div className="banner-cards-wrapper">
                {filteredBanners.map((banner) => (
                  <BannerCard
                    key={banner.id}
                    banner={banner}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteBanner}
                    onToggleActive={handleToggleActive}
                  />
                ))}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      {/* Banner Modal */}
      {showModal && (
        <BannerModal
          isOpen={showModal}
          onClose={handleModalClose}
          banner={editingBanner}
          onSave={handleSaveBanner}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}

export default BannerContentAdmin;